import { Controller } from '@hotwired/stimulus'
import Sortable from 'sortablejs'

// このコントローラーは Trestle の form dialog 内で D&D 用セクションに配置される。
// container / facilityForms は data-controller 配下の直接子孫のため this.element ベースで探索し、
// noteField だけは data-controller ルート外（モーダル上部の textarea）にあるため、
// form をたどって id="new_tour_plan_note" で直接探索する。
export default class extends Controller {
  static values = {
    previousNoteUrl: String,
    dragDisabled: Boolean,
  }

  connect () {
    const container = this.container()
    if (container && !this.dragDisabledValue) {
      this.initializeSortable(container)
    }
  }

  disconnect () {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = undefined
    }
  }

  // container / facilityForms は this.element の直接子孫。form 全体を scope にする必要なし。
  container () {
    return this.element.querySelector('[data-new-tour-plan-facility-container]')
  }

  facilityForms () {
    return Array.from(this.element.querySelectorAll('[data-new-tour-plan-facility-form]'))
  }

  // モーダル内のヒアリング項目フィールドをまとめて取得（controller スコープ外）。
  // Issue #12961 コメント追加要件: note 以外の見学候補日・人数等もコピー対象。
  hearingFields () {
    const form = this.element.closest('form')
    if (!form) return null
    return {
      note: form.querySelector('#new_tour_plan_note'),
      proposed_date: form.querySelector('#new_tour_plan_proposed_date'),
      number_of_visitors: form.querySelector('#new_tour_plan_number_of_visitors'),
      detail: form.querySelector('#new_tour_plan_detail'),
      transportation_id: form.querySelector('#new_tour_plan_transportation_id'),
      accompanying: form.querySelector('#new_tour_plan_accompanying'),
      food_preference: form.querySelector('#new_tour_plan_food_preference'),
    }
  }

  initializeSortable (container) {
    this.sortable = Sortable.create(container, {
      animation: 150,
      handle: '.facility-order-badge',
      ghostClass: 'sortable-ghost',
      onEnd: () => this.updateOrderBadgesAndPositions(),
    })
  }

  updateOrderBadgesAndPositions () {
    // NOTE: Ruby 側 Admin::Ca::Request::TourPlan::Modal::Component::CIRCLED_NUMBERS と揃える。
    // 片方のみ更新されると番号表示が drift するため、拡張時は両方を同じ値で更新すること。
    const circledNumbers = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩',
      '⑪', '⑫', '⑬', '⑭', '⑮', '⑯', '⑰', '⑱', '⑲', '⑳']
    this.facilityForms().forEach((formEl, newIndex) => {
      const positionInput = formEl.querySelector('input[name*="[position]"]')
      if (positionInput) {
        positionInput.value = newIndex
      }
      const badge = formEl.querySelector('.facility-order-badge')
      if (badge) {
        badge.textContent = circledNumbers[newIndex] || `(${newIndex + 1})`
      }
    })
  }

  appendFacilityForm (event) {
    const forms = this.facilityForms()
    const lastFacilityFormIndex = forms.length - 1
    const lastFacilityForm = forms[forms.length - 1]
    if (!lastFacilityForm) return

    const appendForm = lastFacilityForm.cloneNode(true)
    const appendFormIndex = lastFacilityFormIndex + 1
    appendForm.dataset.formIndex = appendFormIndex

    const renameAndReset = (fieldName, value) => {
      const el = appendForm.querySelector(`#${this.facilityFormId(lastFacilityFormIndex, fieldName)}`)
      if (!el) return
      el.name = this.facilityFormName(appendFormIndex, fieldName)
      el.id = this.facilityFormId(appendFormIndex, fieldName)
      if (el.tagName === 'SELECT') {
        el.selectedIndex = null
      } else if (el.type === 'checkbox') {
        el.checked = false
      } else {
        el.value = value
      }
    }

    renameAndReset('id', null)
    renameAndReset('ca_request_facility_id', null)
    renameAndReset('condition_fixed', false)
    renameAndReset('note', null)
    renameAndReset('position', appendFormIndex)
    renameAndReset('score', '')

    // 新規施設はスコアなし (-) で初期化する。色クラスと progress bar もリセット。
    const scoreRow = appendForm.querySelector('.facility-score-row')
    if (scoreRow) {
      scoreRow.classList.remove(
        'facility-score--high',
        'facility-score--mid',
        'facility-score--low',
      )
      scoreRow.classList.add('facility-score--none')
      const scoreValue = scoreRow.querySelector('.facility-score-value')
      if (scoreValue) { scoreValue.textContent = '-' }
      const scoreBar = scoreRow.querySelector('.facility-score-bar')
      if (scoreBar) { scoreBar.remove() }
    }
    // 「施設スコアから登録」バッジは新規には不要（現在は表示自体してない想定だが念のため）
    const sourceBadge = appendForm.querySelector('.facility-source-badge')
    if (sourceBadge) { sourceBadge.remove() }

    const appendDeleteFlagContainer = appendForm.querySelector('.delete-flag-container')
    if (appendDeleteFlagContainer) { appendDeleteFlagContainer.remove() }

    const container = this.container()
    if (container) {
      container.appendChild(appendForm)
      this.updateOrderBadgesAndPositions()
    }
  }

  // 「1つ上からヒアリング情報をコピー」ボタン押下時のハンドラ。
  // Issue #12961 コメント追加要件:
  // - コピー対象: note + 見学候補日 + 見学人数 + 同行者詳細 + 見学時の交通手段 +
  //              入居者の同行 + 食事の希望
  // - 上書き確認ダイアログは廃止し、コピー後に「コピーしました」通知のみ
  async copyPreviousNote (event) {
    event.preventDefault()
    if (event.currentTarget.disabled) return

    const url = this.previousNoteUrlValue
    if (!url) {
      alert('参照先が設定されていません')
      return
    }

    const fields = this.hearingFields()
    if (!fields || !fields.note) {
      console.error('copyPreviousNote: hearing fields not found')
      alert('ヒアリングフォームが見つかりませんでした')
      return
    }

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!response.ok) {
        console.error('copyPreviousNote fetch failed:', response.status, response.statusText, url)
        alert(`直前のヒアリング事項を取得できませんでした (status: ${response.status})`)
        return
      }
      const data = await response.json()
      if (data.is_template_only) {
        alert('1つ上のタスクが未入力か、存在しないためコピーできません')
        return
      }

      const attrs = data.attributes || {}
      this.applyHearingAttributes(fields, attrs)
      // 確認ダイアログは出さず、コピー後に通知（Save New Tour Plan を押すまでDB反映されない前提）
      alert('コピーしました')
    } catch (error) {
      console.error('copyPreviousNote error:', error, 'url:', url)
      alert('ネットワークエラーが発生しました')
    }
  }

  applyHearingAttributes (fields, attrs) {
    // text / textarea / number 系
    this.setFieldValue(fields.note, attrs.note)
    this.setFieldValue(fields.proposed_date, attrs.proposed_date)
    this.setFieldValue(fields.number_of_visitors, attrs.number_of_visitors)
    this.setFieldValue(fields.detail, attrs.detail)
    // select の transportation_id
    this.setSelectValue(fields.transportation_id, attrs.transportation_id)
    // checkbox 2項目
    this.setCheckboxValue(fields.accompanying, attrs.accompanying)
    this.setCheckboxValue(fields.food_preference, attrs.food_preference)
  }

  setFieldValue (el, value) {
    if (!el) return
    el.value = value == null ? '' : String(value)
  }

  setSelectValue (el, value) {
    if (!el) return
    el.value = value == null ? '' : String(value)
    // select2 を使う場合は change イベントで再描画
    if (window.jQuery) { window.jQuery(el).trigger('change') }
  }

  setCheckboxValue (el, value) {
    if (!el) return
    el.checked = value === true || value === 'true' || value === 1 || value === '1'
  }

  facilityFormName (i, name) {
    return `new_tour_plan[facility_tour_plans_attributes][${i}][${name}]`
  }

  facilityFormId (i, name) {
    return `new_tour_plan_facility_tour_plans_attributes_${i}_${name}`
  }
}
